import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/Providers/ToastProvider';
import axiosInstance from 'config/axiosConfig';
import { useLeadsStore } from 'store/leadsStore';
import { Lead } from 'types/Lead';

function updateLead(lead: Partial<Lead>): Promise<Lead> {
    return axiosInstance.patch(`/inquires/${lead.id}`, { status: lead.status }).then((res) => res.data.data);
}

export const useUpdateLead = () => {
    const queryClient = useQueryClient();
    const { showMessage } = useToast();
    const { currentPage, status } = useLeadsStore();
    return useMutation({
        mutationFn: (lead: Partial<Lead>) => updateLead(lead),
        onMutate: async (data) => {
            await queryClient.cancelQueries({
                queryKey: ['leads', currentPage, status],
            });

            const previousData = queryClient.getQueryData(['leads', currentPage, status]);

            queryClient.setQueryData(['leads', currentPage, status], (oldData: any) => {
                if (oldData.inquiries.find((lead: Lead) => lead.id === data.id)?.status === data.status) return oldData;

                const newData = { ...oldData };

                newData.inquiries =
                    status !== data.status && status !== ''
                        ? newData.inquiries.filter((lead: Lead) => lead.id !== data.id)
                        : newData.inquiries.map((lead: Lead) => (lead.id === data.id ? { ...lead, status: data.status } : lead));
                newData.total = status !== data.status && status !== '' ? newData.total - 1 : newData.total;
                showMessage('Lead updated successfully', 'success');
                return newData;
            });

            return () => queryClient.setQueryData(['leads', currentPage, status], previousData);
        },
        onError: (error, _, rollback) => {
            rollback?.();
            showMessage(error.message, 'error');
        },
        onSuccess: (data, variables) => {
            queryClient.resetQueries({ queryKey: ['leads', currentPage, variables.status] });
        },
    });
};
